// src/utils/maps.js
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase.js';

// Cache em memória para acesso ultra-rápido
const memoryCache = new Map();

// Estatísticas de economia
let stats = {
  apiCalls: 0,
  cacheHits: 0,
  totalSavings: 0 // em USD (aproximado)
};

// Log de inicialização
console.log('💰 Sistema de Cache do Google Maps inicializado!');
console.log('📊 Economia esperada: 80-90% nos custos da Distance Matrix API');

/**
 * Normaliza um endereço para criar uma chave de cache consistente
 * Substitui caracteres que causam problemas no Firestore (/, \, etc)
 */
function normalizeAddress(address) {
  return address
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[,.\s]/g, '') // Remove vírgulas, pontos e espaços
    .replace(/[/\\]/g, '-'); // Substitui barras (/ e \) por hífen para preservar números de apartamento
}

/**
 * Cria uma chave única para o par origem-destino
 */
function createCacheKey(origin, destination) {
  const normOrigin = normalizeAddress(origin);
  const normDest = normalizeAddress(destination);
  // Ordena para que A->B e B->A usem a mesma chave (mesma distância)
  const [first, second] = [normOrigin, normDest].sort();
  return `${first}|||${second}`;
}

/**
 * Busca no cache (memória + Firestore)
 */
async function getCachedDistance(cacheKey) {
  // 1. Verifica cache em memória (instantâneo)
  if (memoryCache.has(cacheKey)) {
    stats.cacheHits++;
    stats.totalSavings += 0.005; // ~US$0.005 por chamada evitada
    console.log(`💰 Cache hit! Economia total: ~US$${stats.totalSavings.toFixed(2)} | ${stats.cacheHits} chamadas evitadas`);
    return memoryCache.get(cacheKey);
  }

  // 2. Verifica cache no Firestore
  try {
    const docRef = doc(db, 'distanceCache', cacheKey);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Salva em memória para próxima vez
      memoryCache.set(cacheKey, data.minutes);
      stats.cacheHits++;
      stats.totalSavings += 0.005;
      console.log(`💰 Cache hit (Firestore)! Economia total: ~US$${stats.totalSavings.toFixed(2)} | ${stats.cacheHits} chamadas evitadas`);
      return data.minutes;
    }
  } catch (error) {
    console.warn('Erro ao buscar cache no Firestore:', error);
  }

  return null;
}

/**
 * Salva no cache (memória + Firestore)
 */
async function saveCachedDistance(cacheKey, minutes, origin, destination) {
  // Salva em memória
  memoryCache.set(cacheKey, minutes);

  // Salva no Firestore
  try {
    await setDoc(doc(db, 'distanceCache', cacheKey), {
      origin,
      destination,
      minutes,
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString()
    });
  } catch (error) {
    console.warn('Erro ao salvar cache no Firestore:', error);
  }
}

/**
 * Calcula a distância usando a Distance Matrix API
 * Agora com cache inteligente para economizar custos!
 */
export async function getTravelMinutes(origin, destination) {
  const cacheKey = createCacheKey(origin, destination);

  // Verifica cache primeiro
  const cached = await getCachedDistance(cacheKey);
  if (cached !== null) {
    return cached;
  }

  // Cache miss - precisa chamar a API
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      return reject(new Error('Google Maps API não carregada'));
    }

    stats.apiCalls++;
    console.log(`🌐 API call #${stats.apiCalls} - De: ${origin.substring(0, 30)}... Para: ${destination.substring(0, 30)}...`);

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        // sem departure_time -> retorna duration "padrão" sem trânsito
      },
      async (response, status) => {
        if (status !== 'OK') return reject(new Error(status));
        const element = response.rows[0].elements[0];
        if (element.status !== 'OK') return resolve(0);

        // duration.value em segundos -> minutos (arredonda para baixo)
        const minutes = Math.floor(element.duration.value / 60);

        // Salva no cache para futuras consultas
        await saveCachedDistance(cacheKey, minutes, origin, destination);

        resolve(minutes);
      }
    );
  });
}

